import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addGroomingFieldTestFeedback,
  addGroomingHandoffNote,
  commitGroomingMediaUpload,
  createGroomingMediaUploadTicket,
  listGroomingSessions,
  startGroomingIntake,
  updateGroomingStatus,
  updateGroomingWorkflowStep,
  upsertGroomingReportCard,
} from './grooming-api';
import type {
  GroomingIntakeInput,
  GroomingFieldTestFeedbackInput,
  GroomingMediaKind,
  GroomingMediaUploadInput,
  GroomingMediaUploadTicket,
  GroomingReportCard,
  GroomingReportInput,
  GroomingSessionDetail,
  GroomingSessionStatus,
  GroomingSessionSummary,
  GroomingWorkflowStepStatus,
} from './types';

export const groomingKeys = {
  all: ['grooming'] as const,
  sessions: (branchId: string) => [...groomingKeys.all, 'sessions', branchId] as const,
};

export function useGroomingSessions(branchId: string | undefined) {
  return useQuery<GroomingSessionSummary[]>({
    queryKey: groomingKeys.sessions(branchId ?? '__none__'),
    queryFn: () => {
      if (!branchId) return Promise.resolve([]);
      return listGroomingSessions(branchId);
    },
    enabled: Boolean(branchId),
  });
}

export function useStartGroomingIntake(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<GroomingSessionDetail, Error, {
    bookingId: string;
    input: GroomingIntakeInput;
  }>({
    mutationFn: ({ bookingId, input }) => startGroomingIntake(bookingId, input),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: groomingKeys.sessions(branchId) });
      }
    },
  });
}

export function useUpdateGroomingStatus(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<GroomingSessionDetail, Error, {
    sessionId: string;
    status: GroomingSessionStatus;
  }>({
    mutationFn: ({ sessionId, status }) => updateGroomingStatus(sessionId, status),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: groomingKeys.sessions(branchId) });
      }
    },
  });
}

export function useUpsertGroomingReport(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<GroomingReportCard, Error, {
    sessionId: string;
    input: GroomingReportInput;
  }>({
    mutationFn: ({ sessionId, input }) => upsertGroomingReportCard(sessionId, input),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: groomingKeys.sessions(branchId) });
      }
    },
  });
}

export function useUpdateGroomingWorkflow(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<GroomingSessionDetail, Error, {
    sessionId: string;
    stepKey: string;
    status: GroomingWorkflowStepStatus;
  }>({
    mutationFn: ({ sessionId, stepKey, status }) =>
      updateGroomingWorkflowStep(sessionId, stepKey, status),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: groomingKeys.sessions(branchId) });
      }
    },
  });
}

export function useAddGroomingHandoffNote(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<GroomingSessionDetail, Error, {
    sessionId: string;
    note: string;
  }>({
    mutationFn: ({ sessionId, note }) => addGroomingHandoffNote(sessionId, note),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: groomingKeys.sessions(branchId) });
      }
    },
  });
}

export function useAddGroomingFieldTestFeedback(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<GroomingSessionDetail, Error, {
    sessionId: string;
    input: GroomingFieldTestFeedbackInput;
  }>({
    mutationFn: ({ sessionId, input }) => addGroomingFieldTestFeedback(sessionId, input),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: groomingKeys.sessions(branchId) });
      }
    },
  });
}

export function useCreateGroomingMediaUploadTicket() {
  return useMutation<GroomingMediaUploadTicket, Error, {
    sessionId: string;
    input: GroomingMediaUploadInput;
  }>({
    mutationFn: ({ sessionId, input }) => createGroomingMediaUploadTicket(sessionId, input),
  });
}

export function useCommitGroomingMediaUpload(branchId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<GroomingReportCard, Error, {
    sessionId: string;
    kind: GroomingMediaKind;
    objectKey: string;
  }>({
    mutationFn: ({ sessionId, kind, objectKey }) =>
      commitGroomingMediaUpload(sessionId, kind, objectKey),
    onSuccess: () => {
      if (branchId) {
        qc.invalidateQueries({ queryKey: groomingKeys.sessions(branchId) });
      }
    },
  });
}
